import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { llmLogsData } from '../../../data/data_llm';
import { promptData } from '../../../data/data_prompts';
import './AdministrationLLMLogs.css';

function AdministrationLLMLogs() {
  const [searchId, setSearchId] = useState('');
  const [logs, setLogs] = useState([]);
  const [latestInstances, setLatestInstances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchType, setSearchType] = useState('short_id'); // 'short_id' or 'instance_id'
  const [collapsedLogs, setCollapsedLogs] = useState(new Set());
  const [showSearch, setShowSearch] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [copyMessages, setCopyMessages] = useState({});
  const [currentShortId, setCurrentShortId] = useState('');
  
  // Rerun slideout state
  const [showRerunSlideout, setShowRerunSlideout] = useState(false);
  const [rerunData, setRerunData] = useState({
    instance_id: '',
    system_prompt: '',
    prompt: '',
    type: '',
    structure: '',
    ref_name: '',
    ref_value: '',
    connection_id: ''
  });
  const [rerunLoading, setRerunLoading] = useState(false);
  const [rerunResult, setRerunResult] = useState(null);
  const [llmModels, setLlmModels] = useState([]);
  const [selectedLogId, setSelectedLogId] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipText, setTooltipText] = useState('');
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [isFormCollapsed, setIsFormCollapsed] = useState(false);

  useEffect(() => {
    loadLatestInstances();
    loadLLMModels();
  }, []);

  const loadLatestInstances = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await llmLogsData.getLatestLLMLogs();
      if (response.success) {
        setLatestInstances(response.instances || []);
      } else {
        setError('Failed to load latest instances');
      }
    } catch (err) {
      setError('Error loading latest instances: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // const handleSearch = async (preserveCollapsedState = false) => {
  //   if (!searchId.trim()) {
  //     setError('Please enter a search ID');
  //     return;
  //   }

  //   setLoading(true);
  //   setError('');
  //   try {
  //     const response = await llmLogsData.getSearchLLMLogs(
  //       searchType === 'instance_id' ? searchId : null,
  //       searchType === 'short_id' ? searchId : null
  //     );
      
  //     if (response.success) {
  //       const newLogs = response.logs || [];
        
  //       // Only reset collapsed state for new searches, not refreshes
  //       if (!preserveCollapsedState) {
  //         setCollapsedLogs(new Set());
  //       }
  //       // For refreshes, we keep the existing collapsedLogs state
        
  //       setLogs(newLogs);
  //       setShowSearch(false); // Hide search when logs are displayed
  //       if (!preserveCollapsedState) {
  //         setTypeFilter('all'); // Only reset type filter for new searches
  //       }
        
  //       // Find and store the short_id if searching by instance_id
  //       if (searchType === 'instance_id' && newLogs.length > 0) {
  //         const instanceId = newLogs[0].instance_id;
  //         const matchingInstance = latestInstances.find(inst => inst.instance_id === instanceId);
  //         setCurrentShortId(matchingInstance ? matchingInstance.short_id : '');
  //       } else if (searchType === 'short_id') {
  //         setCurrentShortId(searchId);
  //       }
  //     } else {
  //       setError('Failed to search logs');
  //     }
  //   } catch (err) {
  //     setError('Error searching logs: ' + err.message);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleSearch = async (preserveCollapsedState = false) => {
    if (!searchId.trim()) {
      setError('Please enter a search ID');
      return;
    }
  
    setLoading(true);
    setError('');
    try {
      const response = await llmLogsData.getSearchLLMLogs(
        searchType === 'instance_id' ? searchId : null,
        searchType === 'short_id' ? searchId : null
      );
  
      if (!response.success) {
        setError('Failed to search logs');
        return;
      }
  
      const newLogs = response.logs || [];
  
      // === NEW COLLAPSE-STATE LOGIC ===
      if (preserveCollapsedState) {
        // capture previous state
        const prevCollapsed = collapsedLogs;
        const prevCount     = logs.length;
  
        // if user had collapsed ALL old logs, collapse all new ones
        if (prevCollapsed.size === prevCount) {
          setCollapsedLogs(new Set(newLogs.map(l => l.id)));
        } else {
          // otherwise, only keep collapse on IDs that persisted
          const stillCollapsed = new Set();
          newLogs.forEach(l => {
            if (prevCollapsed.has(l.id)) stillCollapsed.add(l.id);
          });
          setCollapsedLogs(stillCollapsed);
        }
      } else {
        // brand-new search → clear collapse state
        setCollapsedLogs(new Set());
      }
      // ================================
  
      setLogs(newLogs);
      setShowSearch(false);
  
      if (!preserveCollapsedState) {
        setTypeFilter('all');
      }
  
      // …the rest of your ID‐tracking code stays the same…
      if (searchType === 'instance_id' && newLogs.length > 0) {
        const instanceId = newLogs[0].instance_id;
        const matchingInstance = latestInstances.find(inst => inst.instance_id === instanceId);
        setCurrentShortId(matchingInstance ? matchingInstance.short_id : '');
      } else if (searchType === 'short_id') {
        setCurrentShortId(searchId);
      }
  
    } catch (err) {
      setError('Error searching logs: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleInstanceClick = async (instance) => {
    setSearchId(instance.short_id || instance.instance_id);
    setSearchType(instance.short_id ? 'short_id' : 'instance_id');
    setCurrentShortId(instance.short_id || '');
    
    setLoading(true);
    setError('');
    try {
      const response = await llmLogsData.getSearchLLMLogs(
        instance.instance_id,
        instance.short_id
      );
      
      if (response.success) {
        setLogs(response.logs || []);
        setShowSearch(false); // Hide search when logs are displayed
        setTypeFilter('all'); // Reset type filter when new logs are loaded
        // When clicking a new instance, we want to show all logs
        setCollapsedLogs(new Set());
      } else {
        setError('Failed to load logs for this instance');
      }
    } catch (err) {
      setError('Error loading logs: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    if (searchId.trim()) {
      // Preserve collapsed state during refresh
      handleSearch(true);
    } else {
      loadLatestInstances();
    }
  };

  const handleClear = () => {
    setSearchId('');
    setLogs([]);
    setError('');
    setCollapsedLogs(new Set());
    setShowSearch(true); // Show search again
    setTypeFilter('all'); // Reset type filter
    setCurrentShortId(''); // Reset current short ID
    loadLatestInstances();
  };

  const toggleSearch = () => {
    setShowSearch(!showSearch);
  };

  // Track collapsed state by log ID which should be stable across refreshes
  const toggleLogCollapse = (log) => {
    const newCollapsedLogs = new Set(collapsedLogs);
    if (newCollapsedLogs.has(log.id)) {
      newCollapsedLogs.delete(log.id);
    } else {
      newCollapsedLogs.add(log.id);
    }
    setCollapsedLogs(newCollapsedLogs);
  };

  const toggleAllLogs = () => {
    if (collapsedLogs.size === filteredLogs.length) {
      // All are collapsed, expand all
      setCollapsedLogs(new Set());
    } else {
      // Some or none are collapsed, collapse all
      const allIds = filteredLogs.map(log => log.id);
      setCollapsedLogs(new Set(allIds));
    }
  };

  const handleTypeFilterChange = (event) => {
    setTypeFilter(event.target.value);
    // Maintain collapsed state when filter changes
    // Only reset if we're switching from a filtered view to "all" or vice versa
    // This preserves the user's collapse preferences
  };

  const copyToClipboard = async (text, type, logId) => {
    try {
      await navigator.clipboard.writeText(text);
      
      // Show success message
      const messageKey = `${logId}-${type.toLowerCase()}`;
      setCopyMessages(prev => ({
        ...prev,
        [messageKey]: true
      }));

      // Hide message after 2 seconds
      setTimeout(() => {
        setCopyMessages(prev => ({
          ...prev,
          [messageKey]: false
        }));
      }, 2000);

    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      
      // Show success message even for fallback
      const messageKey = `${logId}-${type.toLowerCase()}`;
      setCopyMessages(prev => ({
        ...prev,
        [messageKey]: true
      }));

      setTimeout(() => {
        setCopyMessages(prev => ({
          ...prev,
          [messageKey]: false
        }));
      }, 2000);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Rerun functions
  const loadLLMModels = async () => {
    try {
      const response = await promptData.getLLMModels();
      if (response.success) {
        setLlmModels(response.llmModels || []);
      }
    } catch (err) {
      console.error('Error loading LLM models:', err);
    }
  };

  const handleRerunClick = async (log) => {
    // Prepare type - add _rerun suffix if not already present
    let rerunType = log.type;
    if (!rerunType.endsWith('_rerun')) {
      rerunType = rerunType + '_rerun';
    }

    // Find the matching LLM model connection_id based on the log's llm_model_name
    let connectionId = '';
    if (log.llm_model_name && llmModels.length > 0) {
      const matchingModel = llmModels.find(model => model.name === log.llm_model_name);
      console.log('Matching model found:', matchingModel);
      if (matchingModel) {
        connectionId = matchingModel.connection_id;
      }
    }
    console.log('Final connection_id:', connectionId);

    setRerunData({
      instance_id: log.instance_id,
      system_prompt: log.system_prompt || '',
      prompt: log.prompt || '',
      type: rerunType,
      structure: log.structure || '',
      ref_name: 'instance_id',
      ref_value: log.instance_id,
      connection_id: connectionId
    });
    setSelectedLogId(log.id);
    setSelectedLog(log);
    setShowRerunSlideout(true);
    setRerunResult(null);
  };

  const handleRerunSubmit = async () => {
    setRerunLoading(true);
    setRerunResult(null);
    
    try {
      const response = await llmLogsData.rerunLLMQuery(
        rerunData.instance_id,
        rerunData.connection_id, // Use the selected LLM model
        rerunData.prompt,
        rerunData.system_prompt,
        rerunData.structure,
        rerunData.type,
        rerunData.ref_name,
        rerunData.ref_value
      );
      
      setRerunResult(response);
      // Collapse the form after successful response
      if (response.success) {
        setIsFormCollapsed(true);
      }
    } catch (err) {
      setRerunResult({
        success: false,
        error: err.message
      });
    } finally {
      setRerunLoading(false);
    }
  };

  const handleCloseRerunSlideout = () => {
    setShowRerunSlideout(false);
    setRerunData({
      instance_id: '',
      system_prompt: '',
      prompt: '',
      type: '',
      structure: '',
      ref_name: '',
      ref_value: '',
      connection_id: ''
    });
    setRerunResult(null);
    setSelectedLogId(null);
    setSelectedLog(null);
    setIsFormCollapsed(false);
  };

  const showCustomTooltip = (event, text) => {
    console.log('Showing LLM logs tooltip:', text);
    // Get the help icon element (the span with the llm-logs-help-icon class)
    const helpIcon = event.currentTarget;
    const rect = helpIcon.getBoundingClientRect();
    
    // Position tooltip above the help icon, centered horizontally
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 35
    });
    setTooltipText(text);
    setShowTooltip(true);
  };

  const hideCustomTooltip = () => {
    setShowTooltip(false);
  };

  // Get unique types from logs
  const uniqueTypes = [...new Set(logs.map(log => log.type))].sort();

  // Sort logs by created_at in descending order (newest first) and then filter
  const sortedLogs = [...logs].sort((a, b) => {
    // First sort by created_at timestamp
    const dateCompare = new Date(b.created_at) - new Date(a.created_at);
    if (dateCompare !== 0) return dateCompare;
    // If timestamps are equal, sort by ID to ensure stable order
    return b.id - a.id; // Assuming id is a number
  });
  
  // Filter logs based on type filter
  const filteredLogs = typeFilter === 'all' 
    ? sortedLogs 
    : sortedLogs.filter(log => log.type === typeFilter);

  return (
    <div className="administration-llm-logs">
      {/* Top Container - Fixed Header */}
      <div className="llm-logs-header">
        <h2>LLM Logs</h2>
        <div className="llm-logs-actions">
          {logs.length > 0 && (
            <button 
              className="btn btn-secondary" 
              onClick={toggleSearch}
            >
              <i className="ti ti-search"></i>
              {showSearch ? 'Hide Search' : 'Show Search'}
            </button>
          )}
          <button 
            className="btn btn-secondary" 
            onClick={handleRefresh}
            disabled={loading}
          >
            <i className="ti ti-refresh"></i>
            Refresh
          </button>
          <button 
            className="btn btn-secondary" 
            onClick={handleClear}
            disabled={loading}
          >
            <i className="ti ti-x"></i>
            Clear
          </button>
        </div>
      </div>

      {/* Bottom Container - Scrollable Content */}
      <div className="llm-logs-content-container">
        {showSearch && (
          <div className="llm-logs-search-section">
            <div className="search-form">
              <div className="search-input-group">
                <select 
                  value={searchType} 
                  onChange={(e) => setSearchType(e.target.value)}
                  className="search-type-select"
                >
                  <option value="short_id">Short ID</option>
                  <option value="instance_id">Instance ID</option>
                </select>
                <input
                  type="text"
                  value={searchId}
                  onChange={(e) => setSearchId(e.target.value)}
                  placeholder={`Enter ${searchType === 'short_id' ? 'short ID' : 'instance ID'}...`}
                  className="search-input"
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch(false)}
                />
                <button 
                  className="btn btn-primary" 
                  onClick={() => handleSearch(false)}
                  disabled={loading || !searchId.trim()}
                >
                  <i className="ti ti-search"></i>
                  Search
                </button>
              </div>
            </div>

            {error && (
              <div className="error-message">
                <i className="ti ti-alert-circle"></i>
                {error}
              </div>
            )}
          </div>
        )}

        <div className="llm-logs-content">
          {!searchId && !logs.length && (
            <div className="latest-instances-section">
              <h3>Latest LLM Instances</h3>
              {loading ? (
                <div className="loading">Loading latest instances...</div>
              ) : latestInstances.length > 0 ? (
                <>
                  <div className="instances-grid">
                    {latestInstances.map((instance, index) => (
                      <div 
                        key={index} 
                        className="instance-card"
                        onClick={() => handleInstanceClick(instance)}
                      >
                        <div className="instance-id">
                          <strong>Short ID:</strong> {instance.short_id}
                        </div>
                        <div className="instance-full-id">
                          <strong>Instance ID:</strong> {instance.instance_id}
                        </div>
                        <div className="instance-click-hint">
                          Click to view logs
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="instances-info">
                    <p>Showing the latest {latestInstances.length} instances. To view logs from older instances, use the search above with the instance ID or short ID.</p>
                  </div>
                </>
              ) : (
                <div className="no-instances">No recent instances found</div>
              )}
            </div>
          )}

          {logs.length > 0 && (
            <div className="logs-section">
              <div className="logs-section-header">
                <h3>LLM Logs ({filteredLogs.length} found) - Instance: {filteredLogs.length > 0 ? `${filteredLogs[0].instance_id} (${currentShortId})` : ''}</h3>
                <div className="logs-section-controls">
                  <select 
                    value={typeFilter} 
                    onChange={handleTypeFilterChange}
                    className="type-filter-select"
                  >
                    <option value="all">Show All Types</option>
                    {uniqueTypes.map(type => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  <button 
                    className="btn btn-secondary" 
                    onClick={toggleAllLogs}
                  >
                    <i className="ti ti-arrows-vertical"></i>
                    {collapsedLogs.size === filteredLogs.length ? 'Expand All' : 'Collapse All'}
                  </button>
                </div>
              </div>
              <div className="logs-list">
                {filteredLogs.map((log, index) => {
                  const isCollapsed = collapsedLogs.has(log.id);
                  const isEven = index % 2 === 0;
                  
                  return (
                    <div key={log.id} className={`log-item ${isEven ? 'log-item-even' : 'log-item-odd'} ${isCollapsed ? 'log-item-collapsed' : ''}`}>
                      <div 
                        className="log-header"
                        onClick={() => toggleLogCollapse(log)}
                      >
                        <div className="log-meta">
                          <span className="log-id">ID: {log.id}</span>
                          <span className="log-type">{log.type}</span>
                          <span className="log-model">{log.llm_model_name}</span>
                          <span className="log-date">{formatDate(log.created_at)}</span>
                        </div>
                        <div className="log-header-actions">
                          <button 
                            className="rerun-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRerunClick(log);
                            }}
                            title="Rerun this LLM query"
                          >
                            <i className="ti ti-refresh"></i>
                          </button>
                          <button className="collapse-toggle">
                            <i className={`ti ${isCollapsed ? 'ti-chevron-down' : 'ti-chevron-up'}`}></i>
                          </button>
                        </div>
                      </div>
                      
                      {!isCollapsed && (
                        <div className="log-content">
                          {log.system_prompt && (
                            <div className="log-system-prompt">
                              <div className="log-section-header">
                                <h4>System Prompt</h4>
                                <div className="copy-section">
                                  <button 
                                    className="copy-button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      copyToClipboard(log.system_prompt, 'System Prompt', log.id);
                                    }}
                                    title="Copy system prompt to clipboard"
                                  >
                                    <i className="ti ti-copy"></i>
                                  </button>
                                  {copyMessages[`${log.id}-system prompt`] && (
                                    <span className="copy-success-message">Copied!</span>
                                  )}
                                </div>
                              </div>
                              <div className="log-text-content system-prompt-content">
                                {log.system_prompt}
                              </div>
                            </div>
                          )}
                          
                          <div className="log-prompt">
                            <div className="log-section-header">
                              <h4>Prompt</h4>
                              <div className="copy-section">
                                <button 
                                  className="copy-button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    copyToClipboard(log.prompt, 'Prompt', log.id);
                                  }}
                                  title="Copy prompt to clipboard"
                                >
                                  <i className="ti ti-copy"></i>
                                </button>
                                {copyMessages[`${log.id}-prompt`] && (
                                  <span className="copy-success-message">Copied!</span>
                                )}
                              </div>
                            </div>
                            <div className="log-text-content">
                              {log.prompt}
                            </div>
                          </div>
                          
                          {log.structure && (
                            <div className="log-structure">
                              <div className="log-section-header">
                                <h4>Structure</h4>
                                <div className="copy-section">
                                  <button 
                                    className="copy-button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      copyToClipboard(log.structure, 'Structure', log.id);
                                    }}
                                    title="Copy structure to clipboard"
                                  >
                                    <i className="ti ti-copy"></i>
                                  </button>
                                  {copyMessages[`${log.id}-structure`] && (
                                    <span className="copy-success-message">Copied!</span>
                                  )}
                                </div>
                              </div>
                              <div className="log-text-content structure-content">
                                {log.structure}
                              </div>
                            </div>
                          )}
                          
                          <div className="log-response">
                            <div className="log-section-header">
                              <h4>Response</h4>
                              <div className="copy-section">
                                <button 
                                  className="copy-button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    copyToClipboard(log.response || 'No response data', 'Response', log.id);
                                  }}
                                  title="Copy response to clipboard"
                                >
                                  <i className="ti ti-copy"></i>
                                </button>
                                {copyMessages[`${log.id}-response`] && (
                                  <span className="copy-success-message">Copied!</span>
                                )}
                              </div>
                            </div>
                            <div className="log-text-content">
                              {log.response || 'No response data'}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Rerun Slideout */}
      {showRerunSlideout && (
        <div className="rerun-slideout-overlay" onClick={handleCloseRerunSlideout}>
          <div className="rerun-slideout" onClick={(e) => e.stopPropagation()}>
            <div className="rerun-slideout-header">
              <h3>Rerun LLM Query - Log ID: {selectedLogId}</h3>
              <button className="close-button" onClick={handleCloseRerunSlideout}>
                <i className="ti ti-x"></i>
              </button>
            </div>
            
            <div className="rerun-slideout-content">
              {/* Collapsible Form Section */}
              <div className={`rerun-form-section ${isFormCollapsed ? 'collapsed' : ''}`}>
                <div className="rerun-form-header" onClick={() => setIsFormCollapsed(!isFormCollapsed)}>
                  <h4>Rerun Parameters</h4>
                  <button className="collapse-toggle">
                    <i className={`ti ${isFormCollapsed ? 'ti-chevron-down' : 'ti-chevron-up'}`}></i>
                  </button>
                </div>
                
                <div className="rerun-form-content">
                  <div className="form-group">
                    <label>Instance ID</label>
                    <input
                      type="text"
                      value={rerunData.instance_id}
                      onChange={(e) => setRerunData({...rerunData, instance_id: e.target.value})}
                      className="form-input"
                    />
                  </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>LLM Model</label>
                  <select
                    value={rerunData.connection_id}
                    onChange={(e) => setRerunData({...rerunData, connection_id: e.target.value})}
                    className="form-select"
                  >
                    <option value="">Select LLM Model</option>
                    {llmModels.length === 0 ? (
                      <option value="" disabled>
                        Loading LLM models...
                      </option>
                    ) : (
                      llmModels.map((model) => (
                        <option key={model.id || model.connection_id} value={model.connection_id}>
                          {model.name}
                        </option>
                      ))
                    )}
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Type</label>
                  <input
                    type="text"
                    value={rerunData.type}
                    onChange={(e) => setRerunData({...rerunData, type: e.target.value})}
                    className="form-input"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>System Prompt</label>
                <textarea
                  value={rerunData.system_prompt}
                  onChange={(e) => setRerunData({...rerunData, system_prompt: e.target.value})}
                  className="form-textarea system-prompt-textarea"
                  rows="1"
                />
              </div>
              
              <div className="form-group">
                <label>Prompt</label>
                <textarea
                  value={rerunData.prompt}
                  onChange={(e) => setRerunData({...rerunData, prompt: e.target.value})}
                  className="form-textarea"
                  rows="12"
                />
              </div>
              
              <div className="form-group">
                <label>Structure</label>
                <textarea
                  value={rerunData.structure}
                  onChange={(e) => setRerunData({...rerunData, structure: e.target.value})}
                  className="form-textarea"
                  rows="4"
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                                  <label>
                  Ref Name
                  <span 
                    className="llm-logs-help-icon" 
                    onMouseEnter={(e) => showCustomTooltip(e, "These values are pass through values sent to the LLM to track usage.")}
                    onMouseLeave={hideCustomTooltip}
                  >
                    <i className="ti ti-help"></i>
                  </span>
                </label>
                  <input
                    type="text"
                    value={rerunData.ref_name}
                    onChange={(e) => setRerunData({...rerunData, ref_name: e.target.value})}
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                                  <label>
                  Ref Value
                  <span 
                    className="llm-logs-help-icon" 
                    onMouseEnter={(e) => showCustomTooltip(e, "These values are pass through values sent to the LLM to track usage.")}
                    onMouseLeave={hideCustomTooltip}
                  >
                    <i className="ti ti-help"></i>
                  </span>
                </label>
                  <input
                    type="text"
                    value={rerunData.ref_value}
                    onChange={(e) => setRerunData({...rerunData, ref_value: e.target.value})}
                    className="form-input"
                  />
                </div>
              </div>
            </div>
          </div>
              
          <div className="rerun-actions">
                <button 
                  className="btn btn-primary" 
                  onClick={handleRerunSubmit}
                  disabled={rerunLoading}
                >
                  {rerunLoading ? (
                    <>
                      <i className="ti ti-loader ti-spin"></i>
                      Running...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-refresh"></i>
                      Rerun
                    </>
                  )}
                </button>
                <button 
                  className="btn btn-secondary" 
                  onClick={handleCloseRerunSlideout}
                  disabled={rerunLoading}
                >
                  Close
                </button>
              </div>
              
              {rerunResult && (
                <div className={`rerun-result ${rerunResult.success ? 'success' : 'error'}`}>
                  <h4>{rerunResult.success ? 'Success' : 'Error'}</h4>
                  {rerunResult.success ? (
                    <div className="result-content">
                      <div className="result-timestamp">
                        <strong>Timestamp:</strong> {new Date(rerunResult.timestamp).toLocaleString()}
                      </div>
                      <div className="result-model">
                        <strong>Model:</strong> {llmModels.find(model => model.connection_id == rerunData.connection_id)?.name || rerunData.connection_id || 'Not specified'}
                      </div>
                      <div className="result-response">
                        <strong>Response:</strong>
                        <pre>{rerunResult.response}</pre>
                      </div>
                    </div>
                  ) : (
                    <div className="result-error">
                      {rerunResult.error || 'An error occurred while rerunning the query.'}
                    </div>
                  )}
                </div>
              )}
              
              {/* Previous Result Comparison */}
              {rerunResult && rerunResult.success && selectedLog && (
                <div className="previous-result">
                  <h4>Previous Result (Original Log)</h4>
                  <div className="result-content">
                    <div className="result-timestamp">
                      <strong>Timestamp:</strong> {formatDate(selectedLog.created_at)}
                    </div>
                    <div className="result-model">
                      <strong>Model:</strong> {selectedLog.llm_model_name || 'Not specified'}
                    </div>
                    <div className="result-response">
                      <strong>Response:</strong>
                      <pre>{selectedLog.response || 'No response data'}</pre>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Custom Tooltip */}
      {showTooltip && createPortal(
        <div 
          className="llm-logs-custom-tooltip"
          style={{
            left: tooltipPosition.x,
            top: tooltipPosition.y
          }}
        >
          {tooltipText}
        </div>,
        document.body
      )}
    </div>
  );
}

export default AdministrationLLMLogs; 